import React, { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,

  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush,
} from 'recharts';

interface RevenueData {
  Year: number;
  total_revenue: string;
  total_stores_start: string;
  revenue_per_store: string;
  previous_year_revenue: string | null;
  revenue_growth: string | null;
  revenue_growth_percentage: string | null;
}

interface RevenueGrowthChartProps {
  data: RevenueData[];
}

const formatRevenue = (value: string | null) => {
  if (!value) return 'N/A';
  const num = parseInt(value, 10);
  return `$${(num / 1e9).toFixed(2)}B`;
};

const formatRevenuePerStore = (value: string) => {
  const num = parseFloat(value);
  return `$${num.toFixed(0)}`;
};

const formatPercentage = (value: string | null) => {
  if (!value) return 'N/A';
  return `${parseFloat(value).toFixed(2)}%`;
};

const RevenueGrowthChart: React.FC<RevenueGrowthChartProps> = ({ data }) => {
  // Define colors
  const barColor1 = '#54A7A8';  // Primary bar color for Total Revenue
  const barColor2 = '#3F7D7E';  // Secondary bar color for Revenue Growth
  const lineColor1 = '#2A5354'; // Line color for Revenue per Store
  const lineColor2 = '#54A7A8'; // Line color for Revenue Growth %
  const textColor = '#2A5354';  // Consistent text color

  // Y axis width calculation
  const [yAxisWidth, setYAxisWidth] = useState<number>(60); // initial value

  useEffect(() => {
    const updateYAxisWidth = () => {
      const yAxis = document.getElementsByClassName('recharts-cartesian-axis recharts-yAxis')[0];
      if (yAxis) {
        const yAxisWidth = (yAxis as SVGGraphicsElement)?.getBBox().width;
        setYAxisWidth(yAxisWidth);
      }
    };

    setTimeout(updateYAxisWidth, 0);
    window.addEventListener("resize", updateYAxisWidth);

    return () => {
      window.removeEventListener("resize", updateYAxisWidth);
    };
  }, [data]);



  return (
    <div>
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={data}
          margin={{ top: 20, right: 0, left: 0, bottom: 5 }}
        >

          <XAxis dataKey="Year" />
          <YAxis yAxisId="left" orientation="left" stroke={barColor1} width={yAxisWidth} />
          <YAxis yAxisId="right" orientation="right" stroke={lineColor1} />
          <Tooltip
            contentStyle={{
              backgroundColor: "rgba(255,255,255,0.8)",
              borderColor: "#ccc",
            }}
            formatter={(value: string, name: string) => {
              if (name === "Total Revenue") return formatRevenue(value);
              if (name === "Revenue per Store") return formatRevenuePerStore(value);
              if (name === "Revenue Growth") return formatRevenue(value);
              if (name === "Revenue Growth %") return formatPercentage(value);
              return value;
            }}
          />
          <Brush dataKey="Year" height={12} stroke={barColor1} />
          <Legend />
          <Bar yAxisId="left" dataKey="total_revenue" fill={barColor1} name="Total Revenue">
            {data.map((entry, index) => (
              <text
                key={`total-revenue-${index}`}
                x={index * 300 + 150}
                y={180}
                fill={textColor}
                textAnchor="middle"
                dy={-10}
              >
                {formatRevenue(entry.total_revenue)}
              </text>
            ))}
          </Bar>
          <Bar yAxisId="left" dataKey="revenue_growth" fill={barColor2} name="Revenue Growth">
            {data.map((entry, index) => (
              <text
                key={`revenue-growth-${index}`}
                x={index * 300 + 150}
                y={entry.revenue_growth ? 300 : 350}
                fill={textColor}
                textAnchor="middle"
                dy={-10}
              >
                {formatRevenue(entry.revenue_growth)}
              </text>
            ))}
          </Bar>
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="revenue_per_store"
            stroke={lineColor1}
            name="Revenue per Store"
          >
            {data.map((entry, index) => (
              <text
                key={`revenue-per-store-${index}`}
                x={index * 300 + 150}
                y={100}
                fill={textColor}
                textAnchor="middle"
                dy={-10}
              >
                {formatRevenuePerStore(entry.revenue_per_store)}
              </text>
            ))}
          </Line>
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="revenue_growth_percentage"
            stroke={lineColor2}
            name="Revenue Growth %"
          >
            {data.map((entry, index) => (
              <text
                key={`revenue-growth-percentage-${index}`}
                x={index * 300 + 150}
                y={50}
                fill={textColor}
                textAnchor="middle"
                dy={-10}
              >
                {formatPercentage(entry.revenue_growth_percentage)}
              </text>
            ))}
          </Line>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default RevenueGrowthChart;
